import { useState, useEffect } from "react";
import { Link } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import {
  Wind, Flame, Snowflake, Zap, FlaskConical,
  Search, Filter, ArrowRight, CheckCircle, ChevronRight,
  Phone, Star, Award, Leaf, Settings, Loader2, ImageIcon,
} from "lucide-react";
import { productsService, type Product } from "../../lib/services/productsService";
import type { Models } from "appwrite";

type ProductDoc = Product & Models.Document;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

const FRONTEND_CATEGORIES = ["All", "Air Conditioning", "Heat Pump", "Refrigeration", "Electrical & PLC", "Refrigerant ISO Tank"];

const categoryIcons: Record<string, React.ElementType> = {
  "Air Conditioning": Wind,
  "Heat Pump": Flame,
  "Refrigeration": Snowflake,
  "Electrical & PLC": Zap,
  "Refrigerant ISO Tank": FlaskConical,
};

const badgeColors: Record<string, string> = {
  "Best Seller": "bg-[#E9A319] text-white",
  "Top Rated": "bg-blue-600 text-white",
  "New": "bg-green-500 text-white",
  "Popular": "bg-purple-600 text-white",
  "Pro Choice": "bg-teal-600 text-white",
  "Eco Pick": "bg-emerald-600 text-white",
  "Innovation": "bg-[#1a2d50] text-white",
};

const stats = [
  { icon: Award, value: "500+", label: "Projects Delivered" },
  { icon: Settings, value: "13+", label: "Product Ranges" },
  { icon: Leaf, value: "75%", label: "Energy Savings" },
  { icon: Star, value: "10+", label: "Years Experience" },
];

const FALLBACK_IMG = "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600";

import { SEO } from "./SEO";

export function ProductsPage() {
  const [products, setProducts] = useState<ProductDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    productsService.list()
      .then(setProducts)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = products.filter((p) => {
    if (p.status === "Inactive") return false;
    const matchesCategory = activeCategory === "All" || p.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      p.name.toLowerCase().includes(q) ||
      (p.description ?? "").toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) ||
      (p.features ?? "").toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  return (
    <div>
      <SEO
        title="Engineering-Grade Products | Solace SMART Solutions"
        description="Explore our complete range of premium HVAC systems, heat pumps, refrigeration units, and more. Backed by expert design and lifetime support."
      />
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative py-24 bg-gradient-to-br from-[#1a2d50] via-[#1f3561] to-[#243d6a] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-96 h-96 bg-[#E9A319] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-blue-400 rounded-full blur-3xl" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center justify-center gap-2 text-sm mb-6 w-full">
            <Link to="/" className="text-white/50 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-white/30" />
            <span className="text-[#E9A319]" style={{ fontWeight: 600 }}>Our Products</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-5xl mt-3 mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
            Engineering-Grade Products for Every Application
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-blue-100 max-w-2xl mx-auto text-lg" style={{ lineHeight: 1.8 }}>
            From HVAC systems to refrigerant ISO tanks — explore our complete range of premium products backed by expert design, installation, and lifetime support.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <s.icon className="w-6 h-6 text-[#E9A319] mx-auto mb-2" />
                <div className="text-3xl text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>{s.value}</div>
                <div className="text-blue-200 text-sm mt-0.5">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Sticky Filters ──────────────────────────────────────────── */}
      <section className="sticky top-16 z-40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-border shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-center justify-between">
            <div className="flex gap-2 w-full md:w-auto overflow-x-auto pb-1 md:pb-0 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {FRONTEND_CATEGORIES.map((cat) => {
                const Icon = cat === "All" ? Filter : categoryIcons[cat];
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all border shrink-0 ${activeCategory === cat ? "bg-[#1a2d50] border-[#1a2d50] text-white shadow-md" : "bg-white border-border text-[#1a2d50] hover:bg-muted hover:border-[#1a2d50]/30"}`}
                    style={{ fontWeight: 500 }}
                  >
                    {Icon && <Icon className="w-3.5 h-3.5" />}
                    {cat}
                  </button>
                );
              })}
            </div>
            <div className="relative w-full md:w-80 shrink-0">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full bg-white border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#1a2d50]/20 focus:border-[#1a2d50]/50 shadow-sm transition-all"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Products Grid / Loading / Empty ───────────────────────── */}
      <section className="py-16 bg-[#f8f6f1]">
        <div className="max-w-screen-2xl mx-auto px-6">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-[#1a2d50]/5 animate-pulse">
                  <div className="h-52 bg-gray-100" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-gray-100 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-full" />
                    <div className="h-3 bg-gray-100 rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div initial="hidden" animate="visible" variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {filtered.map((product) => {
                  const ProductIcon = categoryIcons[product.category] ?? Wind;
                  const featureList = (product.features ?? "")
                    .split(",")
                    .map((f) => f.trim())
                    .filter(Boolean)
                    .slice(0, 4);
                  return (
                    <motion.div
                      key={product.$id}
                      variants={fadeUp}
                      layout
                      initial={{ opacity: 0, scale: 0.94 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.94 }}
                      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#1a2d50]/5 hover:shadow-xl transition-all duration-300 group flex flex-col"
                    >
                      {/* Image */}
                      <div className="relative overflow-hidden h-52 shrink-0 bg-gray-100">
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-12 h-12 text-gray-200" />
                          </div>
                        )}
                        {product.badge && (
                          <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs ${badgeColors[product.badge] ?? "bg-gray-700 text-white"}`} style={{ fontWeight: 600 }}>
                            {product.badge}
                          </span>
                        )}
                        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-lg flex items-center gap-1.5">
                          <ProductIcon className="w-3.5 h-3.5 text-[#1a2d50]" />
                          <span className="text-xs text-[#1a2d50]" style={{ fontWeight: 600 }}>{product.category}</span>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="text-[#1a2d50] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: "1rem", lineHeight: 1.4 }}>
                          {product.name}
                        </h3>
                        {product.description && (
                          <p className="text-muted-foreground text-sm mb-4 flex-1" style={{ lineHeight: 1.65 }}>
                            {product.description}
                          </p>
                        )}

                        {featureList.length > 0 && (
                          <div className="grid grid-cols-2 gap-1.5 mb-5">
                            {featureList.map((f) => (
                              <div key={f} className="flex items-start gap-1.5">
                                <CheckCircle className="w-3.5 h-3.5 text-[#E9A319] shrink-0 mt-0.5" />
                                <span className="text-xs text-[#1a2d50]/80">{f}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="pt-4 border-t border-border mt-auto">
                          <Link
                            to="/contact"
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#1a2d50] text-white py-2.5 rounded-xl text-sm hover:bg-[#243d6a] transition-all"
                            style={{ fontWeight: 600 }}
                          >
                            <Phone className="w-3.5 h-3.5" />
                            Request a Quote
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-24">
              <Filter className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">No products found matching your criteria.</p>
              <button
                onClick={() => { setActiveCategory("All"); setSearchQuery(""); }}
                className="mt-4 text-[#E9A319] text-sm underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Why Solace ──────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Why Choose Us</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl text-[#1a2d50] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Complete Turnkey Engineering Solutions
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Settings, title: "Design to Commissioning", desc: "Every product comes with complete project execution — site survey, engineering design, installation, commissioning, and handover training." },
              { icon: Award, title: "Japanese Precision Standards", desc: "We follow SQDMC culture (Safety, Quality, Delivery, Morale & Cost), ensuring every installation meets the highest engineering benchmarks." },
              { icon: Leaf, title: "Energy & Eco Efficiency", desc: "From inverter HVAC systems to heat pump water heaters — our products are selected for maximum efficiency and minimum environmental impact." },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="bg-[#f8f6f1] rounded-2xl p-8 border border-[#1a2d50]/5">
                <div className="w-12 h-12 bg-[#E9A319]/10 rounded-xl flex items-center justify-center mb-5">
                  <item.icon className="w-6 h-6 text-[#E9A319]" />
                </div>
                <h3 className="text-[#1a2d50] text-lg mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>{item.title}</h3>
                <p className="text-muted-foreground text-sm" style={{ lineHeight: 1.75 }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA Banner ──────────────────────────────────────────────── */}
      <section className="py-20 bg-[#f8f6f1]">
        <div className="max-w-screen-2xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#1a2d50] to-[#243d6a] rounded-3xl p-12 md:p-16 text-white text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 right-0 w-96 h-96 bg-[#E9A319] rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-400 rounded-full blur-3xl" />
            </div>
            <div className="relative z-10">
              <span className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Get Started Today</span>
              <h2 className="text-3xl md:text-4xl mt-3 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>Have a Project in Mind?</h2>
              <p className="text-blue-100 max-w-2xl mx-auto mb-8" style={{ lineHeight: 1.8 }}>
                Our engineers will conduct a free site survey, prepare optimum-cost designs, and deliver a transparent proposal — end to end, with no obligation.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-10">
                {["Free Site Survey", "Optimum-Cost Design", "Professional Installation", "After-Sales AMC"].map((item) => (
                  <span key={item} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#E9A319]" />{item}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact" className="inline-flex items-center gap-2 bg-[#E9A319] text-white px-8 py-4 rounded-full hover:bg-[#d4920f] transition-all shadow-xl" style={{ fontWeight: 600 }}>
                  <Phone className="w-4 h-4" />Get a Free Quote<ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/services" className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 rounded-full hover:bg-white/10 transition-all" style={{ fontWeight: 600 }}>
                  Explore Services<ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
